(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-aquaphoria-v5-3d]';
  var mounted = new WeakMap();
  var runtimeFallbackHandlers = new WeakMap();
  var runtimeReadyHandlers = new WeakMap();
  var runtimeReadyTimers = new WeakMap();

  function dispatch(root, state, detail) {
    root.dataset.aquaphoriaV5State = state;
    root.dispatchEvent(new CustomEvent('aquaphoria:v5-state', {
      bubbles: true,
      detail: Object.assign({ state: state }, detail || {})
    }));
  }

  function supportsWebGL() {
    try {
      var canvas = document.createElement('canvas');
      return !!(
        window.WebGLRenderingContext &&
        (canvas.getContext('webgl2') || canvas.getContext('webgl'))
      );
    } catch (error) {
      return false;
    }
  }

  function removeRuntimeFallbackHandler(root) {
    var handler = runtimeFallbackHandlers.get(root);
    if (!handler) return;
    root.removeEventListener('aquaphoria:v5-runtime-fallback', handler);
    runtimeFallbackHandlers.delete(root);
  }

  function removeRuntimeReadyHandler(root) {
    var handler = runtimeReadyHandlers.get(root);
    if (handler) {
      window.removeEventListener('aquaphoria:v5-runtime-ready', handler);
      runtimeReadyHandlers.delete(root);
    }

    var timer = runtimeReadyTimers.get(root);
    if (timer) {
      window.clearTimeout(timer);
      runtimeReadyTimers.delete(root);
    }
  }

  function disposeRoot(root) {
    var handle = mounted.get(root);
    if (handle && typeof handle.unmount === 'function') {
      handle.unmount();
    }
    mounted.delete(root);
    removeRuntimeFallbackHandler(root);
    removeRuntimeReadyHandler(root);
    delete root.dataset.aquaphoriaV5Mounting;
    delete root.dataset.aquaphoriaV5Initialized;
  }

  function installRuntimeFallbackHandler(root) {
    removeRuntimeFallbackHandler(root);

    var handler = function (event) {
      var reason = event && event.detail && event.detail.reason
        ? String(event.detail.reason)
        : 'runtime-fallback';

      disposeRoot(root);
      dispatch(root, 'fallback', { reason: reason });
    };

    root.addEventListener('aquaphoria:v5-runtime-fallback', handler);
    runtimeFallbackHandlers.set(root, handler);
  }

  function initRoot(root) {
    if (!root || root.dataset.aquaphoriaV5Initialized === 'true') return;

    root.dataset.aquaphoriaV5Initialized = 'true';

    if (document.documentElement.dataset.aqExperience !== 'immersive') {
      dispatch(root, 'fallback', { reason: 'simple-experience' });
      return;
    }
    var enabled = root.dataset.enabled === 'true';
    var reducedMotion = !!(
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );

    if (!enabled) {
      dispatch(root, 'disabled');
      return;
    }

    if (reducedMotion) {
      dispatch(root, 'fallback', { reason: 'reduced-motion' });
      return;
    }

    var compactViewport = !!(
      window.matchMedia &&
      window.matchMedia('(max-width: 749px)').matches
    );

    // Phones keep the polished static aquarium presentation and skip the
    // heavyweight 3D runtime entirely. This removes the runtime bundle and
    // GLB from the critical mobile path while preserving content and nav.
    if (compactViewport) {
      dispatch(root, 'fallback', { reason: 'compact-viewport' });
      return;
    }

    if (!supportsWebGL()) {
      dispatch(root, 'fallback', { reason: 'webgl-unavailable' });
      return;
    }

    var bundleUrl = root.dataset.bundleUrl || '';
    if (!bundleUrl) {
      dispatch(root, 'fallback', { reason: 'runtime-missing' });
      return;
    }

    installRuntimeFallbackHandler(root);
    dispatch(root, 'loading');

    function mountReadyRuntime() {
      var runtime = window.AquaphoriaV5RuntimeModule;
      if (!runtime || typeof runtime.mountAquaphoriaV5 !== 'function') {
        return false;
      }
      if (root.dataset.aquaphoriaV5Mounting === 'true' || mounted.has(root)) {
        return true;
      }

      removeRuntimeReadyHandler(root);
      root.dataset.aquaphoriaV5Mounting = 'true';

      var specimenModelUrl = root.dataset.specimenModelUrl || '';

      Promise.resolve(runtime.mountAquaphoriaV5(root, {
        quality: root.dataset.quality || 'auto',
        mode: root.dataset.aquaphoriaV5Mode || 'day',
        specimenModelUrl: specimenModelUrl,
        fishTextures: {
          aoi: root.dataset.fishAoiUrl || '',
          neptune: root.dataset.fishNeptuneUrl || '',
          rayquaza: root.dataset.fishRayquazaUrl || '',
          deepsea: root.dataset.fishDeepseaUrl || ''
        }
      }))
        .then(function (handle) {
          delete root.dataset.aquaphoriaV5Mounting;
          var normalizedHandle = typeof handle === 'function'
            ? { unmount: handle }
            : handle;
          mounted.set(root, normalizedHandle || null);
          dispatch(root, 'mounted');
        })
        .catch(function (error) {
          delete root.dataset.aquaphoriaV5Mounting;
          console.error('[Aquaphoria V5] 3D runtime failed; keeping fallback.', error);
          removeRuntimeFallbackHandler(root);
          dispatch(root, 'fallback', { reason: 'runtime-error' });
        });

      return true;
    }

    if (mountReadyRuntime()) return;

    var readyHandler = function () {
      mountReadyRuntime();
    };
    runtimeReadyHandlers.set(root, readyHandler);
    window.addEventListener('aquaphoria:v5-runtime-ready', readyHandler, { once: true });

    var readyTimer = window.setTimeout(function () {
      removeRuntimeReadyHandler(root);
      if (!mounted.has(root) && root.dataset.aquaphoriaV5Mounting !== 'true') {
        removeRuntimeFallbackHandler(root);
        dispatch(root, 'fallback', { reason: 'runtime-timeout' });
      }
    }, 12000);
    runtimeReadyTimers.set(root, readyTimer);
  }

  function scan(scope) {
    var root = scope || document;
    if (root.matches && root.matches(ROOT_SELECTOR)) {
      initRoot(root);
    }

    if (root.querySelectorAll) {
      root.querySelectorAll(ROOT_SELECTOR).forEach(initRoot);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      scan(document);
    }, { once: true });
  } else {
    scan(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    scan(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var section = event.target;
    if (!section) return;

    if (section.matches && section.matches(ROOT_SELECTOR)) {
      disposeRoot(section);
    }

    if (section.querySelectorAll) {
      section.querySelectorAll(ROOT_SELECTOR).forEach(disposeRoot);
    }
  });
})();
